import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Boxes, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/labels";
import { notifyCatalogChanged } from "@/context/CatalogContext";
import { catalogIcon } from "@/lib/catalogIcons";
import { Switch } from "@/components/ui/switch";
import { LoadingState, PageHeader, ReasonDialog, StatePill, apiErrorMessage } from "@/components/admin/AdminUI";

const ACCESS_LABELS = { tenant: "Delegável", platform: "Plataforma", personal: "Pessoal" };

export default function AdminCatalog() {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    api.get("/catalog").then(({ data: payload }) => setData(payload)).catch((err) => toast.error(apiErrorMessage(err)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = async (entry, enabled, reason = "") => {
    try {
      await api.put(`/catalog/${entry.key}`, { enabled, reason });
      toast.success(`${entry.label} ${enabled ? "habilitado" : "desabilitado"} na plataforma.`);
      notifyCatalogChanged();
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
      throw err;
    }
  };

  if (!data) return <LoadingState />;
  const groups = Object.entries(data.categories).map(([category, title]) => ({
    title,
    items: data.entries.filter((entry) => entry.category === category),
  }));

  return (
    <div className="space-y-5" data-testid="admin-catalog">
      <PageHeader
        icon={Boxes}
        title="Catálogo de módulos"
        description="26 módulos da plataforma. Desabilitar um módulo o remove do menu e bloqueia sua API para todos — inclusive administradores."
      />
      {groups.map((group) => (
        <div key={group.title} className="rounded-xl border border-border bg-card">
          <h2 className="border-b border-border px-4 py-2.5 text-sm font-semibold">{group.title} ({group.items.length})</h2>
          <ul className="divide-y divide-border/60">
            {group.items.map((entry) => {
              const Icon = catalogIcon(entry.icon);
              return (
                <li key={entry.key} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`catalog-row-${entry.key}`}>
                  <div className="flex min-w-0 items-start gap-3">
                    <Icon size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                        {entry.label}
                        <code className="text-[10px] text-muted-foreground">{entry.key}</code>
                        <StatePill tone={entry.access === "platform" ? "info" : "muted"}>{ACCESS_LABELS[entry.access]}</StatePill>
                        {entry.protected && <StatePill tone="muted"><Lock size={10} className="mr-1" />Protegido</StatePill>}
                        {entry.blocked_by && entry.blocked_by !== entry.key && <StatePill tone="warning">Depende de {entry.blocked_by}</StatePill>}
                      </p>
                      <p className="text-xs text-muted-foreground">{entry.description}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {entry.tenants_contracted !== null && entry.tenants_contracted !== undefined && `${entry.tenants_contracted} empresa(s) contratam · `}
                        {entry.status_updated_at && `alterado em ${formatDate(entry.status_updated_at)} por ${entry.status_updated_by || "—"}`}
                        {entry.status_reason && ` — ${entry.status_reason}`}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={entry.enabled}
                    disabled={entry.protected}
                    onCheckedChange={(checked) => (checked ? apply(entry, true) : setPending(entry))}
                    aria-label={`${entry.enabled ? "Desabilitar" : "Habilitar"} ${entry.label}`}
                    data-testid={`catalog-toggle-${entry.key}`}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <ReasonDialog
        open={Boolean(pending)}
        onOpenChange={(open) => !open && setPending(null)}
        title={pending ? `Desabilitar ${pending.label}` : ""}
        description="O módulo some do menu de todos os usuários e sua API passa a responder 403 até ser reabilitado."
        confirmLabel="Desabilitar"
        destructive
        onConfirm={(reason) => apply(pending, false, reason)}
        testId="catalog-disable-dialog"
      />
    </div>
  );
}
